import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import type { Session as SupabaseSession, User } from "@supabase/supabase-js";
import {
  offerSkus,
  PaperKingdomProjectSchema,
  createProject as createBlankProject,
  type AuditEntry,
  type OfferSku,
  type PaperKingdomProject,
  type PortalSnapshot,
} from "../domain/schema";
import { blueprints } from "../data/catalog";
import { cloudPortalRepository } from "../platform/cloudPortalRepository";
import { portalRepository } from "../platform/portalRepository";

const DEMO_EMAIL = "publisher@demo.paperkingdom";
const ADMIN_EMAIL = "admin@demo.paperkingdom";

type SaveStatus = "idle" | "saving" | "saved" | "error";
type AuthStatus = "loading" | "signed-out" | "ready";
type StorageMode = "cloud" | "local";
type ImportStrategy = "keep-both" | "replace";

interface Session {
  email: string;
  role: "buyer" | "admin";
}

interface PortalContextValue {
  session: Session | null;
  authStatus: AuthStatus;
  authError: string | null;
  passwordRecovery: boolean;
  storageMode: StorageMode;
  canAdmin: boolean;
  saveStatus: SaveStatus;
  projects: PaperKingdomProject[];
  allProjects: PaperKingdomProject[];
  entitlements: OfferSku[];
  allEntitlements: Record<string, OfferSku[]>;
  auditLog: AuditEntry[];
  registerWithPassword: (
    email: string,
    password: string,
  ) => Promise<{ needsEmailConfirmation: boolean }>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  completePasswordRecovery: (password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setPreviewRole: (role: "buyer" | "admin") => void;
  setCurrentOfferAccess: (sku: OfferSku, enabled: boolean) => void;
  createProject: (name?: string) => PaperKingdomProject;
  createExampleProject: (blueprintId?: string) => PaperKingdomProject;
  updateProject: (
    projectId: string,
    updater: (project: PaperKingdomProject) => PaperKingdomProject,
  ) => void;
  deleteProject: (projectId: string) => void;
  importProject: (
    input: unknown,
    strategy?: ImportStrategy,
  ) => { project: PaperKingdomProject; conflict: boolean };
  grantOffer: (email: string, sku: OfferSku) => void;
  revokeOffer: (email: string, sku: OfferSku) => void;
}

const defaultSnapshot: PortalSnapshot = {
  schemaVersion: 1,
  session: null,
  entitlements: {
    [DEMO_EMAIL]: [...offerSkus],
    [ADMIN_EMAIL]: [...offerSkus],
  },
  projects: [],
  auditLog: [],
};

function loadLocalSnapshot(): PortalSnapshot {
  const loaded = portalRepository.load(defaultSnapshot);
  return {
    ...loaded,
    entitlements: {
      ...defaultSnapshot.entitlements,
      ...loaded.entitlements,
    },
  };
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "The cloud workspace could not be reached.";
}

const PortalContext = createContext<PortalContextValue | null>(null);

export function PortalProvider({ children }: PropsWithChildren) {
  const initialLocalSnapshot = useMemo(loadLocalSnapshot, []);
  const [snapshot, setSnapshot] = useState<PortalSnapshot>(initialLocalSnapshot);
  const [authStatus, setAuthStatus] = useState<AuthStatus>(
    cloudPortalRepository.configured && !initialLocalSnapshot.session
      ? "loading"
      : "ready",
  );
  const [authError, setAuthError] = useState<string | null>(null);
  const [passwordRecovery, setPasswordRecovery] = useState(
    () =>
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("recovery") === "1",
  );
  const [storageMode, setStorageMode] = useState<StorageMode>(
    cloudPortalRepository.configured ? "cloud" : "local",
  );
  const [cloudUser, setCloudUser] = useState<User | null>(null);
  const [canAdmin, setCanAdmin] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const lastQueuedSnapshot = useRef(snapshot);
  const hydrationSequence = useRef(0);
  const activeAccountEmail = useRef(
    initialLocalSnapshot.session?.email.trim().toLowerCase() ?? null,
  );

  const hydrateCloudSession = useCallback(async (session: SupabaseSession | null) => {
    const sequence = ++hydrationSequence.current;
    if (!session) {
      activeAccountEmail.current = null;
      setCloudUser(null);
      setCanAdmin(false);
      setAuthError(null);
      setSnapshot((current) => ({ ...current, session: null }));
      setAuthStatus("signed-out");
      return;
    }

    const user = session.user;
    const email = user.email?.trim().toLowerCase();
    if (!email) {
      activeAccountEmail.current = null;
      setAuthError("This account does not have an email address.");
      setAuthStatus("signed-out");
      return;
    }

    if (activeAccountEmail.current !== email) {
      setAuthStatus("loading");
    }
    setAuthError(null);

    try {
      const workspace = await cloudPortalRepository.loadWorkspace(user);
      if (sequence !== hydrationSequence.current) return;

      let projects = workspace.projects;
      if (
        !projects.length &&
        workspace.entitlements.includes("paper-kingdom-fe")
      ) {
        const cached = loadLocalSnapshot().projects.filter(
          (project) =>
            project.ownerEmail === email || project.ownerEmail === DEMO_EMAIL,
        );
        if (cached.length) {
          const migrated = cached.map((project) => ({
            ...project,
            ownerEmail: email,
          }));
          await Promise.all(
            migrated.map((project) =>
              cloudPortalRepository.saveProject(user, project),
            ),
          );
          projects = migrated;
        }
      }

      if (sequence !== hydrationSequence.current) return;
      activeAccountEmail.current = email;
      setCloudUser(user);
      setCanAdmin(workspace.role === "admin");
      setStorageMode("cloud");
      const nextSnapshot: PortalSnapshot = {
        schemaVersion: 1,
        session: { email, role: "buyer" },
        entitlements: {
          ...workspace.allEntitlements,
          [email]: workspace.entitlements,
        },
        projects,
        auditLog: workspace.auditLog,
      };
      try {
        portalRepository.save(nextSnapshot);
      } catch {
        // The cloud workspace remains authoritative when the browser cache is unavailable.
      }
      setSnapshot(nextSnapshot);
      setAuthStatus("ready");
    } catch (error) {
      if (sequence !== hydrationSequence.current) return;
      const cached = loadLocalSnapshot();
      activeAccountEmail.current = email;
      setCloudUser(user);
      setCanAdmin(false);
      setStorageMode("local");
      setSnapshot({
        ...cached,
        session: { email, role: "buyer" },
        projects: cached.projects.filter((project) => project.ownerEmail === email),
      });
      setAuthError(
        `${errorMessage(error)} Your matching on-device cache remains available.`,
      );
      setAuthStatus("ready");
    }
  }, []);

  useEffect(() => {
    if (!cloudPortalRepository.configured) {
      setSnapshot((current) => ({
        ...current,
        session: current.session ?? { email: DEMO_EMAIL, role: "buyer" },
      }));
      setStorageMode("local");
      setCanAdmin(true);
      setAuthStatus("ready");
      return;
    }

    let active = true;
    void cloudPortalRepository
      .getInitialSession()
      .then((session) => {
        if (active) void hydrateCloudSession(session);
      })
      .catch((error) => {
        if (!active) return;
        setAuthError(errorMessage(error));
        setAuthStatus("signed-out");
      });

    const {
      data: { subscription },
    } = cloudPortalRepository.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") setPasswordRecovery(true);
      if (active) void hydrateCloudSession(session);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [hydrateCloudSession]);

  useEffect(() => {
    if (lastQueuedSnapshot.current === snapshot) return;
    lastQueuedSnapshot.current = snapshot;

    setSaveStatus("saving");
    const timer = window.setTimeout(() => {
      try {
        portalRepository.save(snapshot);
      } catch {
        setSaveStatus("error");
        return;
      }

      if (
        storageMode !== "cloud" ||
        !cloudUser ||
        !snapshot.session ||
        !snapshot.entitlements[snapshot.session.email]?.includes("paper-kingdom-fe")
      ) {
        setSaveStatus("saved");
        return;
      }

      void Promise.all(
        snapshot.projects
          .filter((project) => project.ownerEmail === snapshot.session?.email)
          .map((project) =>
            cloudPortalRepository.saveProject(cloudUser, project),
          ),
      )
        .then(() => setSaveStatus("saved"))
        .catch((error) => {
          setAuthError(errorMessage(error));
          setSaveStatus("error");
        });
    }, 550);

    return () => window.clearTimeout(timer);
  }, [cloudUser, snapshot, storageMode]);

  const registerWithPassword = useCallback(async (
    email: string,
    password: string,
  ) => {
    setAuthError(null);
    return cloudPortalRepository.registerWithPassword(email, password);
  }, []);

  const signInWithPassword = useCallback(async (
    email: string,
    password: string,
  ) => {
    setAuthError(null);
    await cloudPortalRepository.signInWithPassword(email, password);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    setAuthError(null);
    await cloudPortalRepository.requestPasswordReset(email);
  }, []);

  const completePasswordRecovery = useCallback(async (password: string) => {
    setAuthError(null);
    await cloudPortalRepository.updatePassword(password);
    setPasswordRecovery(false);

    const url = new URL(window.location.href);
    url.searchParams.delete("recovery");
    window.history.replaceState(window.history.state, "", url);
  }, []);

  const signOut = useCallback(async () => {
    setAuthError(null);
    if (cloudUser && cloudPortalRepository.configured) {
      await cloudPortalRepository.signOut();
      return;
    }
    setCanAdmin(false);
    setSnapshot((current) => ({ ...current, session: null }));
    setAuthStatus(
      cloudPortalRepository.configured ? "signed-out" : "ready",
    );
  }, [cloudUser]);

  const setPreviewRole = useCallback(
    (role: "buyer" | "admin") => {
      if (role === "admin" && !canAdmin) return;
      setSnapshot((current) => ({
        ...current,
        session: current.session
          ? { ...current.session, role }
          : current.session,
      }));
    },
    [canAdmin],
  );

  const createProject = useCallback(
    (name?: string) => {
      if (!snapshot.session) throw new Error("Sign in before creating a project.");
      if (
        !snapshot.entitlements[snapshot.session.email]?.includes("paper-kingdom-fe")
      ) {
        throw new Error("Paper Kingdom Core access is required to create a project.");
      }
      const project = createBlankProject(snapshot.session.email, name);
      setSnapshot((current) => ({
        ...current,
        projects: [project, ...current.projects],
      }));
      return project;
    },
    [snapshot.session],
  );

  const createExampleProject = useCallback(
    (blueprintId = blueprints[0].id) => {
      if (!snapshot.session) throw new Error("Sign in before creating a project.");
      if (
        !snapshot.entitlements[snapshot.session.email]?.includes("paper-kingdom-fe")
      ) {
        throw new Error("Paper Kingdom Core access is required to create a project.");
      }
      const blueprint =
        blueprints.find((item) => item.id === blueprintId) ?? blueprints[0];
      const project = createBlankProject(snapshot.session.email, blueprint.name);
      project.theme = blueprint.positioning;
      project.catalogue = blueprint.titles.map((book, index) => ({
        id: crypto.randomUUID(),
        sequence: index + 1,
        type: project.bookType,
        title: book.title,
        subtitle: book.subtitle,
        differentiator: book.differentiator,
        theme: book.title,
        status: "planned",
      }));
      setSnapshot((current) => ({
        ...current,
        projects: [project, ...current.projects],
      }));
      return project;
    },
    [snapshot.session],
  );

  const updateProject = useCallback(
    (
      projectId: string,
      updater: (project: PaperKingdomProject) => PaperKingdomProject,
    ) => {
      setSnapshot((current) => ({
        ...current,
        projects: current.projects.map((project) => {
          if (
            project.id !== projectId ||
            project.ownerEmail !== current.session?.email
          ) {
            return project;
          }
          const updated = updater(project);
          return {
            ...updated,
            revision: project.revision + 1,
            updatedAt: new Date().toISOString(),
          };
        }),
      }));
    },
    [],
  );

  const deleteProject = useCallback(
    (projectId: string) => {
      setSnapshot((current) => ({
        ...current,
        projects: current.projects.filter(
          (project) =>
            project.id !== projectId ||
            project.ownerEmail !== current.session?.email,
        ),
      }));
      if (storageMode === "cloud" && cloudUser) {
        void cloudPortalRepository.deleteProject(projectId).catch((error) => {
          setAuthError(errorMessage(error));
          setSaveStatus("error");
        });
      }
    },
    [cloudUser, storageMode],
  );

  const importProject = useCallback(
    (input: unknown, strategy: ImportStrategy = "keep-both") => {
      if (!snapshot.session) throw new Error("Sign in before importing a project.");
      if (
        !snapshot.entitlements[snapshot.session.email]?.includes("paper-kingdom-fe")
      ) {
        throw new Error("Paper Kingdom Core access is required to import a project.");
      }
      const parsed = PaperKingdomProjectSchema.parse(input);
      const existing = snapshot.projects.find(
        (project) =>
          project.id === parsed.id &&
          project.ownerEmail === snapshot.session?.email,
      );
      const imported: PaperKingdomProject = {
        ...parsed,
        id:
          existing && strategy === "keep-both"
            ? crypto.randomUUID()
            : parsed.id,
        name:
          existing && strategy === "keep-both"
            ? `${parsed.name} (imported copy)`
            : parsed.name,
        ownerEmail: snapshot.session.email,
        revision:
          existing && strategy === "replace"
            ? Math.max(existing.revision, parsed.revision) + 1
            : parsed.revision,
        updatedAt: new Date().toISOString(),
      };
      setSnapshot((current) => ({
        ...current,
        projects:
          existing && strategy === "replace"
            ? current.projects.map((project) =>
                project.id === imported.id ? imported : project,
              )
            : [imported, ...current.projects],
      }));
      return { project: imported, conflict: Boolean(existing) };
    },
    [snapshot.projects, snapshot.session],
  );

  const writeAudit = useCallback(
    (
      current: PortalSnapshot,
      targetEmail: string,
      action: AuditEntry["action"],
      offerSku?: OfferSku,
    ): AuditEntry[] => [
      {
        id: crypto.randomUUID(),
        actorEmail: current.session?.email ?? ADMIN_EMAIL,
        targetEmail,
        action,
        offerSku,
        createdAt: new Date().toISOString(),
      },
      ...current.auditLog,
    ],
    [],
  );

  const grantOffer = useCallback(
    (email: string, sku: OfferSku) => {
      const target = email.trim().toLowerCase();
      setSnapshot((current) => {
        if (current.session?.role !== "admin") return current;
        const existing = current.entitlements[target] ?? [];
        return {
          ...current,
          entitlements: {
            ...current.entitlements,
            [target]: existing.includes(sku) ? existing : [...existing, sku],
          },
          auditLog: writeAudit(current, target, "grant", sku),
        };
      });
      if (storageMode === "cloud" && cloudUser && canAdmin) {
        void cloudPortalRepository
          .grantOffer(cloudUser, target, sku)
          .catch((error) => setAuthError(errorMessage(error)));
      }
    },
    [canAdmin, cloudUser, storageMode, writeAudit],
  );

  const revokeOffer = useCallback(
    (email: string, sku: OfferSku) => {
      const target = email.trim().toLowerCase();
      setSnapshot((current) => {
        if (current.session?.role !== "admin") return current;
        return {
          ...current,
          entitlements: {
            ...current.entitlements,
            [target]: (current.entitlements[target] ?? []).filter(
              (item) => item !== sku,
            ),
          },
          auditLog: writeAudit(current, target, "revoke", sku),
        };
      });
      if (storageMode === "cloud" && cloudUser && canAdmin) {
        void cloudPortalRepository
          .revokeOffer(target, sku)
          .catch((error) => setAuthError(errorMessage(error)));
      }
    },
    [canAdmin, cloudUser, storageMode, writeAudit],
  );

  const setCurrentOfferAccess = useCallback(
    (sku: OfferSku, enabled: boolean) => {
      const email = snapshot.session?.email;
      if (!email || snapshot.session?.role !== "admin") return;
      if (enabled) grantOffer(email, sku);
      else revokeOffer(email, sku);
    },
    [grantOffer, revokeOffer, snapshot.session],
  );

  const projects = useMemo(
    () =>
      snapshot.projects.filter(
        (project) => project.ownerEmail === snapshot.session?.email,
      ),
    [snapshot.projects, snapshot.session?.email],
  );

  const value = useMemo<PortalContextValue>(
    () => ({
      session: snapshot.session,
      authStatus,
      authError,
      passwordRecovery,
      storageMode,
      canAdmin,
      saveStatus,
      projects,
      allProjects: snapshot.projects,
      entitlements: snapshot.session
        ? snapshot.entitlements[snapshot.session.email] ?? []
        : [],
      allEntitlements: snapshot.entitlements,
      auditLog: snapshot.auditLog,
      registerWithPassword,
      signInWithPassword,
      requestPasswordReset,
      completePasswordRecovery,
      signOut,
      setPreviewRole,
      setCurrentOfferAccess,
      createProject,
      createExampleProject,
      updateProject,
      deleteProject,
      importProject,
      grantOffer,
      revokeOffer,
    }),
    [
      snapshot,
      authStatus,
      authError,
      passwordRecovery,
      storageMode,
      canAdmin,
      saveStatus,
      projects,
      registerWithPassword,
      signInWithPassword,
      requestPasswordReset,
      completePasswordRecovery,
      signOut,
      setPreviewRole,
      setCurrentOfferAccess,
      createProject,
      createExampleProject,
      updateProject,
      deleteProject,
      importProject,
      grantOffer,
      revokeOffer,
    ],
  );

  return (
    <PortalContext.Provider value={value}>
      {children}
    </PortalContext.Provider>
  );
}

export function usePortal() {
  const context = useContext(PortalContext);
  if (!context) throw new Error("usePortal must be used within PortalProvider");
  return context;
}
